import { useState, useEffect, useRef } from "react";
import { useFastagAPI } from "./useFastagAPI";
import toast from "react-hot-toast";
import api from "../utils/api";

// Fire-and-forget: push latest vehicle tracking state to the server so guest share views stay current.
// Retries once after 4s on failure — handles the timing race where the vehicle may not be in the DB yet.
function pushTrackingCache(vehicleId, data) {
  const payload = {
    lat:             data.lat             ?? null,
    lng:             data.lng             ?? null,
    location:        data.location        ?? null,
    status:          data.status          ?? "Offline",
    lastUpdated:     data.lastUpdated     ?? null,
    tollHistory:     data.tollHistory     ?? [],
    route:           data.route           ?? [],
    noRecentData:    data.noRecentData    ?? false,
    isStateLocation: data.isStateLocation ?? false,
  };
  const delays = [5000, 12000, 25000]; // retry at 5s, 12s, 25s
  let attempt = 0;
  const doPost = () => {
    api.post(`/api/vehicles/${vehicleId}/cache`, payload)
      .catch(() => {
        if (attempt < delays.length) {
          setTimeout(doPost, delays[attempt++]);
        }
      });
  };
  doPost();
}

// ── Vehicle data cache (localStorage) ────────────────────────────────────────
const CACHE_KEY = "trackin_vehicle_cache";

function loadCache() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "{}"); }
  catch { return {}; }
}

function saveToCache(vehicleId, data) {
  try {
    const cache = loadCache();
    cache[vehicleId] = { ...data, cachedAt: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {}
}

function getFromCache(vehicleId) {
  return loadCache()[vehicleId] || null;
}

const TEN_MIN    = 10 * 60 * 1000;
const TWO_HOURS  = 2  * 60 * 60 * 1000;
const FOUR_HOURS = 4  * 60 * 60 * 1000;
const FOUR_HALF  = 4.5 * 60 * 60 * 1000;

// When to schedule the next API call based on when the last toll was crossed
function calcNextFetchDelay(vehicle) {
  if (!vehicle.lastUpdated || vehicle.noRecentData || !vehicle.tollHistory?.length) {
    return FOUR_HOURS;
  }
  const elapsedH = (Date.now() - new Date(vehicle.lastUpdated).getTime()) / 3600000;
  if (elapsedH < 1)  return FOUR_HALF;  // just crossed — vehicle likely still moving → next in 4.5h
  if (elapsedH < 3)  return TWO_HOURS;  // crossed 1–3h ago → next in 2h
  return FOUR_HOURS;                     // 3+ hours ago / parked → next in 4h
}

export function useVehicleTracking({
  initialVehicles = [],
  onApiCall = null,
  onVehiclesChange = null,
} = {}) {
  // Hydrate vehicles with last cached data on mount — no API call
  const [vehicles, setVehicles] = useState(() =>
    initialVehicles.map(v => {
      const cached = getFromCache(v.id);
      if (!cached) return { ...v, isTracking: false, status: "Offline" };
      return {
        ...v,
        isTracking:      false,   // always start untracked — user must click Track
        status:          "Offline",
        lat:             cached.lat          ?? v.lat,
        lng:             cached.lng          ?? v.lng,
        location:        cached.location     ?? v.location,
        startLocation:   cached.startLocation ?? v.startLocation,
        lastUpdated:     cached.lastUpdated  ?? v.lastUpdated,
        tollHistory:     cached.tollHistory  ?? [],
        route:           cached.route        ?? [],
        type:            cached.type         ?? v.type,
        noRecentData:    cached.noRecentData ?? false,
        isStateLocation: cached.isStateLocation ?? false,
        cachedAt:        cached.cachedAt     ?? null,
      };
    })
  );
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [loading, setLoading]                 = useState(false);
  const [zoomTrigger, setZoomTrigger]         = useState(0);

  // Online/offline awareness — pause scheduler and block API calls when offline
  const isOnlineRef = useRef(navigator.onLine);
  useEffect(() => {
    const goOnline  = () => { isOnlineRef.current = true; };
    const goOffline = () => { isOnlineRef.current = false; };
    window.addEventListener('online',  goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online',  goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const { verifyVehicle, geocodeStateFromPlate } = useFastagAPI();

  // Stable refs so the scheduler interval doesn't go stale
  const vehiclesRef         = useRef(vehicles);
  const onApiCallRef        = useRef(onApiCall);
  const onVehiclesChangeRef = useRef(onVehiclesChange);

  useEffect(() => { vehiclesRef.current         = vehicles;         }, [vehicles]);
  useEffect(() => { onApiCallRef.current        = onApiCall;        }, [onApiCall]);
  useEffect(() => { onVehiclesChangeRef.current = onVehiclesChange; }, [onVehiclesChange]);

  // Keep selectedVehicle in sync with latest vehicle data
  useEffect(() => {
    if (!selectedVehicle) return;
    const updated = vehicles.find(v => v.id === selectedVehicle.id);
    if (updated) setSelectedVehicle(updated);
  }, [vehicles]); // eslint-disable-line

  // On mount: geocode vehicles with no FASTag data that don't yet have a confirmed state location.
  // Runs for ANY noRecentData vehicle where isStateLocation is false/undefined — this catches
  // vehicles whose cached coords were set by an old (wrong) geocoding pass before state-qualified
  // queries were introduced.
  useEffect(() => {
    const needs = vehicles.filter(v =>
      v.noRecentData && !v.isRCLocation && !v.isStateLocation
    );
    if (!needs.length) return;
    needs.forEach(async v => {
      const geo = await geocodeStateFromPlate(v.plateNumber);
      if (!geo) return;
      setVehicles(prev => {
        const updated = prev.map(vv => {
          if (vv.id !== v.id) return vv;
          const next = {
            ...vv,
            lat: geo.lat, lng: geo.lng,
            location: `${geo.city} (Registration state · no recent tolls)`,
            isStateLocation: true,
          };
          saveToCache(vv.id, {
            lat: next.lat, lng: next.lng, location: next.location,
            startLocation: next.startLocation, lastUpdated: next.lastUpdated,
            tollHistory: next.tollHistory || [], route: next.route || [],
            type: next.type, noRecentData: next.noRecentData,
            isStateLocation: true,
          });
          return next;
        });
        onVehiclesChange?.(updated);
        return updated;
      });
    });
  }, []); // eslint-disable-line

  // ── Silent background refresh (no toast) ────────────────────────────────────
  async function silentRefresh(vehicleId) {
    if (!isOnlineRef.current) return; // skip when offline
    const vehicle = vehiclesRef.current.find(v => v.id === vehicleId);
    if (!vehicle) return;

    if (onApiCallRef.current) {
      const allowed = onApiCallRef.current();
      if (!allowed) return; // quota exhausted
    }

    try {
      const apiData = await verifyVehicle(vehicle.plateNumber);
      let stateGeo = null;
      if (apiData.noRecentData && !vehicle.lat && !vehicle.isRCLocation) {
        stateGeo = await geocodeStateFromPlate(vehicle.plateNumber);
      }
      const delay = calcNextFetchDelay(apiData.noRecentData ? vehicle : apiData);

      setVehicles(prev => {
        const updated = prev.map(v => {
          if (v.id !== vehicleId) return v;
          const noData  = apiData.noRecentData;
          const hasData = !noData && apiData.tollHistory?.length > 0;

          const next = {
            ...v,
            isTracking:      true,
            status:          hasData ? "Active" : "Idle",
            lat:             noData ? (stateGeo?.lat ?? v.lat) : apiData.lat,
            lng:             noData ? (stateGeo?.lng ?? v.lng) : apiData.lng,
            location:        noData
              ? (stateGeo
                  ? `${stateGeo.city} (Registration state · no recent tolls)`
                  : "No FASTag crossings in last 30 days")
              : apiData.location,
            isStateLocation: noData ? (stateGeo ? true : (v.isStateLocation || false)) : false,
            isRCLocation:    noData ? (v.isRCLocation || false) : false,
            startLocation:   hasData ? (apiData.startLocation || apiData.location) : v.startLocation,
            lastUpdated:     hasData ? apiData.lastUpdated : v.lastUpdated,
            tollHistory:     hasData ? apiData.tollHistory : v.tollHistory,
            route:           hasData ? apiData.route       : v.route,
            type:            apiData.type !== "Other" ? apiData.type : v.type,
            noRecentData:    noData,
            verified:        apiData.verified || false,
            lastFastagFetch: Date.now(),
            nextFetchAt:     Date.now() + delay,
          };

          // Persist to localStorage so next page load shows last known data without API call
          saveToCache(v.id, {
            lat: next.lat, lng: next.lng, location: next.location,
            startLocation: next.startLocation, lastUpdated: next.lastUpdated,
            tollHistory: next.tollHistory, route: next.route,
            type: next.type, noRecentData: next.noRecentData,
            isStateLocation: next.isStateLocation ?? false,
          });

          // Push to server DB cache (for guest share views)
          pushTrackingCache(v.id, next);

          return next;
        });
        onVehiclesChangeRef.current?.(updated);
        return updated;
      });
    } catch {
      // On failure push next attempt to 4 hours
      setVehicles(prev => {
        const updated = prev.map(v =>
          v.id !== vehicleId ? v : { ...v, nextFetchAt: Date.now() + FOUR_HOURS }
        );
        onVehiclesChangeRef.current?.(updated);
        return updated;
      });
    }
  }

  // ── Smart scheduler — checks every 60s which tracked vehicles are due ───────
  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now();
      vehiclesRef.current
        .filter(v => v.isTracking && v.nextFetchAt && now >= v.nextFetchAt)
        .forEach(v => silentRefresh(v.id));
    }, 60 * 1000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line

  // ── Add vehicle — 1 API call, then schedule next auto-refresh ───────────────
  async function addVehicle(formData) {
    if (!isOnlineRef.current) {
      toast.error("No internet connection. Please connect and try again.");
      return false;
    }
    setLoading(true);
    // Consume 1 quota call if the user is on a paid plan
    if (onApiCallRef.current) {
      const allowed = onApiCallRef.current();
      if (!allowed) {
        toast.error("API quota exhausted. Top up to add more vehicles.");
        setLoading(false);
        return false;
      }
    }

    // Generate the ID now so we can use it for both the DB pre-save and the local state
    const newId = `v${Date.now()}`;

    // Build the minimal DB payload for the new vehicle — only persisted metadata
    function toDbShape(v) {
      return {
        id: v.id, plateNumber: v.plateNumber, nickname: v.nickname || v.plateNumber,
        type: v.type || "Truck", ownerName: v.ownerName || null,
        contactNumber: v.contactNumber || null, fastagId: v.fastagId || null,
      };
    }

    try {
      // Register the vehicle in the DB BEFORE calling the FASTag API so the
      // server-side fleet ownership check can find it and allow the query.
      const existingForDb = vehiclesRef.current.map(toDbShape);
      const newForDb = toDbShape({
        id: newId, plateNumber: formData.plateNumber,
        nickname: formData.nickname || formData.plateNumber,
        type: formData.vehicleType || "Truck",
        ownerName: formData.ownerName || null, contactNumber: formData.contactNumber || null,
        fastagId: formData.fastagId || null,
      });
      await api.put("/api/vehicles", [...existingForDb, newForDb]);

      // Vehicle is now registered — the ownership check will pass
      const apiData = await verifyVehicle(formData.plateNumber);

      let stateGeo = null;
      if (apiData.noRecentData) {
        stateGeo = await geocodeStateFromPlate(formData.plateNumber);
      }

      const noData = apiData.noRecentData || false;
      const lat    = noData ? (stateGeo?.lat ?? null) : apiData.lat;
      const lng    = noData ? (stateGeo?.lng ?? null) : apiData.lng;
      const delay  = calcNextFetchDelay(noData ? { noRecentData: true } : apiData);

      const newVehicle = {
        id:              newId,
        plateNumber:     formData.plateNumber,
        nickname:        formData.nickname || formData.plateNumber,
        type:            formData.vehicleType || apiData.type || "Other",
        fastagId:        formData.fastagId || null,
        ownerName:       formData.ownerName,
        contactNumber:   formData.contactNumber,
        status:          noData ? "Idle" : "Active",
        isTracking:      true,
        lat, lng,
        location:        noData
          ? (stateGeo ? `${stateGeo.city} (Registration state · no recent tolls)` : "Location unavailable")
          : apiData.location,
        startLocation:   apiData.startLocation || apiData.location,
        lastUpdated:     apiData.lastUpdated || new Date().toISOString(),
        tollHistory:     apiData.tollHistory  || [],
        route:           apiData.route        || (lat && lng ? [{ lat, lng }] : []),
        liveData:        true,
        noRecentData:    noData,
        isStateLocation: noData && !!stateGeo,
        lastFastagFetch: Date.now(),
        nextFetchAt:     Date.now() + delay,
      };

      setVehicles(prev => {
        const updated = [...prev, newVehicle];
        onVehiclesChange?.(updated);
        return updated;
      });
      setSelectedVehicle(newVehicle);

      // Push to server DB cache (for guest share views)
      pushTrackingCache(newVehicle.id, newVehicle);

      if (noData) {
        const locNote = stateGeo ? ` Showing estimated location at ${stateGeo.city} (registration district).` : "";
        toast(`${formData.plateNumber} added — no FASTag crossings in last 30 days.${locNote}`, {
          icon: "⏳",
          style: { background:"#FFFBEB", border:"1px solid #FDE68A", color:"#92400E" },
          duration: 7000,
        });
      } else {
        toast.success(`${formData.plateNumber} added — ${apiData.tollHistory?.length || 0} toll crossing(s) found`);
      }
      return true;
    } catch (err) {
      // FASTag lookup failed — roll back the pre-registered vehicle from DB
      api.put("/api/vehicles", vehiclesRef.current.map(toDbShape)).catch(() => {});
      toast.error(err.message || "Failed to add vehicle");
      return false;
    } finally {
      setLoading(false);
    }
  }

  function startTracking(vehicleId) {
    const vehicle = vehiclesRef.current.find(v => v.id === vehicleId);

    // Mark as tracking immediately (optimistic UI)
    setVehicles(prev => {
      const updated = prev.map(v =>
        v.id === vehicleId
          ? { ...v, isTracking: true, status: v.tollHistory?.length ? "Active" : "Idle" }
          : v
      );
      onVehiclesChangeRef.current?.(updated);
      return updated;
    });
    setZoomTrigger(t => t + 1);

    // Always push current data to server cache so guest share links stay current
    if (vehicle?.lat && vehicle?.lng) {
      pushTrackingCache(vehicleId, vehicle);
    }

    const age = vehicle?.lastFastagFetch
      ? Date.now() - vehicle.lastFastagFetch
      : Infinity;

    if (age < TEN_MIN) {
      // < 10 min — show cached data silently, no API call, no toast
      return;
    }

    if (age < TWO_HOURS) {
      // 10 min – 2 hours — cached data, no API call, inform user
      const mins    = Math.floor(age / 60000);
      const remMins = Math.ceil((TWO_HOURS - age) / 60000);
      const remStr  = remMins >= 60
        ? `${Math.floor(remMins / 60)}h ${remMins % 60}m`
        : `${remMins}m`;
      toast(`Showing data from ${mins}m ago · refreshes in ${remStr}`, {
        icon: "⚡",
        style: { background: "#EFF6FF", border: "1px solid #BFDBFE", color: "#1E40AF" },
        duration: 5000,
      });
      return;
    }

    // ≥ 2 hours or first ever click — fetch fresh data from API
    if (!isOnlineRef.current) {
      toast("You're offline. Showing last known data.", {
        icon: "📡",
        style: { background: "#1E293B", border: "1px solid #334155", color: "#E2E8F0" },
        duration: 4000,
      });
      return;
    }
    silentRefresh(vehicleId);
  }

  function stopTracking(vehicleId) {
    setVehicles(prev => {
      const updated = prev.map(v =>
        v.id === vehicleId ? { ...v, isTracking: false, status: "Offline" } : v
      );
      onVehiclesChange?.(updated);
      return updated;
    });
  }

  function removeVehicle(vehicleId) {
    setVehicles(prev => {
      const updated = prev.filter(v => v.id !== vehicleId);
      onVehiclesChange?.(updated);
      return updated;
    });
    if (selectedVehicle?.id === vehicleId) setSelectedVehicle(null);
    toast.success("Vehicle removed");
  }

  function selectVehicle(vehicle) {
    setSelectedVehicle(prev => prev?.id === vehicle.id ? null : vehicle);
    setZoomTrigger(t => t + 1);
  }

  // ── Manual refresh — 2-hour cooldown, then update nextFetchAt ───────────────
  async function refreshVehicle(vehicleId, force = false) {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    if (!vehicle) return;

    if (!force && vehicle.lastFastagFetch) {
      const age = Date.now() - vehicle.lastFastagFetch;
      if (age < TWO_HOURS) {
        const remMs  = TWO_HOURS - age;
        const remMin = Math.ceil(remMs / 60000);
        const remH   = Math.floor(remMin / 60);
        const remM   = remMin % 60;
        const str    = remH > 0 ? `${remH}h ${remM}m` : `${remMin}m`;
        toast(`Cached data · Next check in ${str}`, {
          icon: "⚡",
          style: { background:"#EFF6FF", border:"1px solid #BFDBFE", color:"#1E40AF" },
          duration: 4000,
        });
        return;
      }
    }

    // Consume 1 quota call before hitting the API
    if (onApiCallRef.current) {
      const allowed = onApiCallRef.current();
      if (!allowed) {
        toast.error("API quota exhausted. Top up to refresh.", { duration: 5000 });
        return;
      }
    }

    const toastId = toast.loading(`Checking FASTag data for ${vehicle.plateNumber}…`);
    try {
      const apiData = await verifyVehicle(vehicle.plateNumber);
      let stateGeo = null;
      if (apiData.noRecentData && !vehicle.lat && !vehicle.isRCLocation) {
        stateGeo = await geocodeStateFromPlate(vehicle.plateNumber);
      }
      const delay = calcNextFetchDelay(apiData.noRecentData ? vehicle : apiData);

      let cachePayload = null;
      setVehicles(prev => {
        const updated = prev.map(v => {
          if (v.id !== vehicleId) return v;
          const keepLocation = apiData.noRecentData;
          const fallbackLat  = stateGeo?.lat ?? v.lat;
          const fallbackLng  = stateGeo?.lng ?? v.lng;
          const fallbackLoc  = stateGeo
            ? `${stateGeo.city} (Registration state · no recent tolls)`
            : "No FASTag crossings in last 60 days";
          const next = {
            ...v,
            lat:             keepLocation ? fallbackLat  : apiData.lat,
            lng:             keepLocation ? fallbackLng  : apiData.lng,
            location:        keepLocation ? fallbackLoc  : apiData.location,
            isRCLocation:    keepLocation ? (v.isRCLocation || false) : false,
            isStateLocation: keepLocation ? (stateGeo ? true : (v.isStateLocation || false)) : false,
            startLocation:   apiData.startLocation || apiData.location,
            lastUpdated:     apiData.lastUpdated,
            tollHistory:     apiData.tollHistory  || [],
            route:           apiData.route        || (fallbackLat ? [{ lat: fallbackLat, lng: fallbackLng }] : []),
            type:            apiData.type !== "Other" ? apiData.type : v.type,
            noRecentData:    apiData.noRecentData || false,
            verified:        apiData.verified     || false,
            lastFastagFetch: Date.now(),
            nextFetchAt:     Date.now() + delay,
          };
          cachePayload = next; // capture the freshly computed state for cache push below
          return next;
        });
        onVehiclesChange?.(updated);
        return updated;
      });

      // Push updated state to server DB cache (for guest share views)
      if (cachePayload) pushTrackingCache(vehicleId, cachePayload);

      if (apiData.noRecentData) {
        toast.error(`Still no FASTag data for ${vehicle.plateNumber}. Checked NETC — 0 crossings.`, { id: toastId, duration: 6000 });
      } else {
        toast.success(`${vehicle.plateNumber} updated — ${apiData.tollHistory?.length || 0} crossing(s) found!`, { id: toastId });
      }
    } catch (err) {
      toast.error(`Refresh failed: ${err.message}`, { id: toastId });
    }
  }

  async function setManualLocation(vehicleId, locationText) {
    if (!locationText?.trim()) return false;
    try {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(locationText.trim() + ", India")}&format=json&limit=1`;
      const res  = await fetch(url, { headers: { "User-Agent": "LorryKhojo/1.0" } });
      const data = await res.json();
      if (!data?.length) {
        toast.error("Location not found. Try a city or highway name.");
        return false;
      }
      const lat = parseFloat(data[0].lat);
      const lng = parseFloat(data[0].lon);
      const loc = locationText.trim();
      setVehicles(prev => {
        const updated = prev.map(v => v.id !== vehicleId ? v : {
          ...v,
          lat, lng,
          location:        loc,
          isStateLocation: false,
          isRCLocation:    false,
          isManualLocation: true,
        });
        onVehiclesChange?.(updated);
        return updated;
      });
      pushTrackingCache(vehicleId, { lat, lng, location: loc, noRecentData: true });
      toast.success(`Location set to ${loc}`);
      return true;
    } catch {
      toast.error("Could not geocode location. Check your connection.");
      return false;
    }
  }

  return {
    vehicles, selectedVehicle, loading, zoomTrigger,
    addVehicle, removeVehicle, selectVehicle, setSelectedVehicle,
    startTracking, stopTracking, refreshVehicle, setManualLocation,
    pushCache: pushTrackingCache,
  };
}
